import * as endpoints from "./endpoints";
import * as models from "./models";
import * as validation from "../validation";
import getUserIds from "../getUserIds";
import logger from "../../lib/logger";
import {AxiosError} from "axios";
import {ErrorInfo} from "react";

function formatStack(
    errMsg: string,
    jsFile?: string,
    lineNumber?: number,
    colNumber?: number,
): string {
    const fileName = jsFile ?? "inlineScriptOrDynamicEvalCode";
    return [errMsg, " Occurred in ", fileName, ":", lineNumber ?? "?", ":", colNumber ?? "?"]
        .join("")
        .replace(/https?:\/\/[^/]+/gi, "");
}

function getAxiosErrorData(err: AxiosError, componentName?: string): models.ErrorData {
    const status = err.response?.status;
    const baseURL = err.config?.baseURL;
    const url = err.config?.url;
    const method = err.config?.method;

    let urlStr = "unknown endpoint";
    if (baseURL || url) {
        if (baseURL) {
            urlStr = baseURL;
        }
        if (url) {
            urlStr = urlStr ? `${urlStr}${url}` : url;
        }
    }

    const statusStr = status ? ` ${status} ` : " API error ";

    const methodStr = method ? ` ${method.toUpperCase()} ` : " ";

    const errorString = `Unhandled${statusStr}from${methodStr}${urlStr}`;

    return {
        visitId: getUserIds().visitID,
        error: errorString,
        url: window.location.href,
        stack: formatStack(err.message, componentName),
    };
}

function getEventErrorData(err: ErrorEvent): models.ErrorData {
    const visitId = getUserIds().visitID;

    const errorObj = err.error;
    const url = err.filename;
    const lineNumber = err.lineno;
    const colNumber = err.colno;
    let error = err.message || errorObj.message || errorObj.name;

    if (errorObj && errorObj.stack) {
        error = [error, "; Stack: ", errorObj.stack, "."].join("");
    }

    const jsFile = (/[^/]+\.js/i.exec(url ?? "") ?? [])[0] ?? "inlineScriptOrDynamicEvalCode";
    const stack = formatStack(error, jsFile, lineNumber, colNumber);

    return {
        visitId,
        error: error ?? errorObj,
        url,
        lineNumber,
        colNumber,
        stack,
    };
}

function getRejectionErrorData(e: PromiseRejectionEvent, componentName?: string) {
    const visitId = getUserIds().visitID;
    return {
        visitId,
        error: e.reason,
        url: window.location.href,
        stack: formatStack(e.reason, componentName),
    };
}

function report(data: models.ErrorData, message: string) {
    if (process.env.NODE_ENV === "development") {
        logger.error("Report error:", data);
        return Promise.resolve();
    }

    return endpoints
        .sendError(data)
        .catch((err) => logger.warn(`Error sending error ${message} data: ${err}`));
}

export function reportUnexpectedNavigation(e: Error) {
    const errorData = {
        visitId: getUserIds().visitID,
        error: e.message,
        url: window.location.href,
        stack: formatStack("Navigation error", e.message),
    };
    return report(errorData, e.message);
}

export function reportAxios(e: AxiosError, componentName: string) {
    const errorData = getAxiosErrorData(e, componentName);
    return report(errorData, e.message);
}

export function reportEvent(e: ErrorEvent) {
    const errorData = getEventErrorData(e);
    return report(errorData, e.message);
}

export function reportRejection(e: PromiseRejectionEvent, componentName: string) {
    if (validation.isAxiosError(e.reason)) {
        return reportAxios(e.reason, componentName);
    }
    const errorData = getRejectionErrorData(e);
    return report(errorData, e.reason);
}

export function reportRender(e: Error, errorInfo: ErrorInfo) {
    const message = e.message;
    return report(
        {
            visitId: getUserIds().visitID,
            error: message,
            stack: errorInfo.componentStack
                .split(/\n/)
                .map((str) => str.trim())
                .join(","),
            url: window.location.href,
        },
        message,
    );
}

export function reportDOMException(e: DOMException, componentName: string) {
    const message = e.message;
    const name = e.name;
    return report(
        {
            visitId: getUserIds().visitID,
            error: name,
            url: window.location.href,
            stack: formatStack(message, componentName),
        },
        name,
    );
}

export function reportUnknown(e: unknown, componentName: string) {
    if (validation.isAxiosError(e)) {
        return reportAxios(e, componentName);
    }
    if (e instanceof PromiseRejectionEvent) {
        return reportRejection(e, componentName);
    }
    if (e instanceof DOMException) {
        return reportDOMException(e, componentName);
    }
    if (e instanceof ErrorEvent) {
        return reportEvent(e);
    }
    const errorData = {
        visitId: getUserIds().visitID,
        error: "Unknown error",
        url: window.location.href,
        stack: formatStack("Unknown error", componentName),
    };
    return report(errorData, "Unknown error");
}
